import type { CaptureResult } from 'posthog-js';
import { resolveModuleForPath } from './taxonomy';

/**
 * Events the SDK emits on its own (not through our `capture()` call sites)
 * that describe a screen and therefore must carry the taxonomy `module` and
 * `route_name` like every event of ours does
 * (docs/product-analytics-contract.md §"Event naming"). Today that is only
 * `$pageleave` (BIL-5257): it is sent by posthog-js on unload / tab-hide, at
 * which point `window.location` is still the page being left, so both are
 * resolved right here. The
 * SDK's identity/flag bookkeeping events (`$identify`, `$set`,
 * `$groupidentify`, `$feature_flag_called`) describe no screen and are
 * exempt — see the contract.
 */
const SDK_SCREEN_EVENTS = new Set(['$pageleave']);

/**
 * `before_send` step, wired ahead of the URL scrub in posthog.ts: stamps
 * `module` and `route_name` on an SDK screen event that names neither (if
 * either is present the event is left untouched). Never drops an event and
 * never throws — any failure returns the input unchanged.
 */
export const stampModuleOnSdkEvents = (
  cr: CaptureResult | null
): CaptureResult | null => {
  if (cr === null) return null;
  try {
    if (!SDK_SCREEN_EVENTS.has(cr.event)) return cr;
    const existing = cr.properties ?? {};
    // Both or neither, so the two never come from different screens.
    if (existing.module !== undefined || existing.route_name !== undefined) {
      return cr;
    }
    const { module, routeName } = resolveModuleForPath(
      window.location.pathname
    );
    return {
      ...cr,
      properties: {
        ...existing,
        module,
        ...(routeName ? { route_name: routeName } : {}),
      },
    };
  } catch {
    return cr;
  }
};
